<script setup lang="ts" generic="T extends string">
import type { Component } from 'vue'
import { useResizeObserver } from '@vueuse/core'

const props = defineProps<{
  items: { id: T; name: string; icon: Component }[]
  modelValue: T
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: T): void
}>()

const navRef = useTemplateRef<HTMLElement>('navRef')
const indicatorTop = ref<number | null>(null)
const isPlaced = ref(false)

const placeIndicator = () => {
  const slot = navRef.value?.querySelector<HTMLElement>(`[data-slot-id="${props.modelValue}"]`)
  if (!slot) return
  indicatorTop.value = slot.offsetTop
  if (!isPlaced.value) requestAnimationFrame(() => (isPlaced.value = true))
}

onMounted(placeIndicator)
watch([() => props.modelValue, () => props.items.map((item) => item.id).join('|')], () => nextTick(placeIndicator))
useResizeObserver(navRef, placeIndicator)
</script>

<template>
  <nav ref="navRef" class="relative -mt-2 flex w-21 shrink-0 flex-col gap-2 pb-1">
    <span
      v-if="indicatorTop !== null"
      aria-hidden="true"
      class="bg-background shadow-rail-active pointer-events-none absolute top-0 left-1/2 h-11 w-12 -translate-x-1/2 rounded-[14px]"
      :class="isPlaced && 'ease-spring transition-transform duration-[420ms] motion-reduce:transition-none'"
      :style="{ transform: `translateY(${indicatorTop}px)` }"
    />
    <Button
      v-for="item in items"
      :key="item.id"
      variant="ghost"
      :aria-current="item.id === modelValue ? 'page' : undefined"
      class="group h-auto flex-col gap-1.5 px-1 py-2 text-xs leading-tight whitespace-normal hover:bg-transparent dark:hover:bg-transparent"
      :class="
        item.id === modelValue
          ? 'text-foreground hover:text-foreground font-semibold'
          : 'text-foreground-secondary hover:text-foreground font-medium'
      "
      @click="emit('update:modelValue', item.id)"
    >
      <span :data-slot-id="item.id" class="flex h-11 w-12 items-center justify-center">
        <span class="relative flex size-full items-center justify-center rounded-[14px]">
          <span
            v-if="item.id !== modelValue"
            aria-hidden="true"
            class="bg-nav-hover ease-editor absolute inset-0 scale-[.86] rounded-[14px] opacity-0 transition-[opacity,scale] duration-200 group-hover:scale-100 group-hover:opacity-100"
          />
          <component
            :is="item.icon"
            class="relative size-5.5 transition-[fill] duration-200"
            :class="item.id === modelValue && 'fill-current/20'"
          />
        </span>
      </span>
      {{ item.name }}
    </Button>
  </nav>
</template>
